import { 
  collection, 
  doc, 
  addDoc, 
  updateDoc, 
  deleteDoc, 
  getDocs, 
  getDoc,
  query,
  orderBy,
  where,
  Timestamp 
} from 'firebase/firestore';
import { ref, uploadBytes, getDownloadURL, deleteObject } from 'firebase/storage';
import { db, storage } from '../firebase';

export interface VideoData {
  id?: string;
  title: string;
  description: string;
  type: 'podcast' | 'video';
  guest?: string;
  badge?: string;
  views: number;
  flames: number;
  publishDate: Date;
  thumbnail: string;
  mostViewed?: boolean;
  trending?: boolean;
  videoUrl?: string;
  thumbnailUrl?: string;
  duration?: number;
  platform?: 'youtube' | 'vimeo' | 'upload';
  platformId?: string;
  region?: string; // Regio waar de video relevant voor is (bijv. 'Nederland', 'Internationaal', etc.)
  regions?: string[]; // Meerdere regio's waar de video relevant voor is
  userId?: string; // ID van de gebruiker die de video heeft geüpload
  uploaderRegions?: string[]; // Regio's van de uploader (voor fallback als video geen regio heeft)
  // ✅ NIEUW: Entity linking voor clubs/masterminds/communities
  entityId?: string; // Link naar entities collection
  entityType?: 'networkclub' | 'mastermind' | 'community'; // Type entity
  clubName?: string; // Naam van de club/entity (voor display en search)
  clubId?: string; // Reverse link naar networkClub document (indien van toepassing)
  uploadedByEntity?: boolean; // Is dit een entity upload? (true = geüpload als club/entity, false = persoonlijk)
}

const VIDEOS_COLLECTION = 'videos';

// Get all videos
export const getAllVideos = async (): Promise<VideoData[]> => {
  try {
    const videosRef = collection(db, VIDEOS_COLLECTION);
    const q = query(videosRef, orderBy('publishDate', 'desc'));
    const querySnapshot = await getDocs(q);
    
    return querySnapshot.docs.map(doc => ({
      id: doc.id,
      ...doc.data(),
      publishDate: doc.data().publishDate?.toDate() || new Date()
    })) as VideoData[];
  } catch (error) {
    console.error('Error getting videos:', error);
    throw error;
  }
};

// Add new video
export const addVideo = async (videoData: Omit<VideoData, 'id'>): Promise<string> => {
  try {
    const videosRef = collection(db, VIDEOS_COLLECTION);
    const docRef = await addDoc(videosRef, {
      ...videoData,
      publishDate: Timestamp.fromDate(videoData.publishDate)
    });
    return docRef.id;
  } catch (error) {
    console.error('Error adding video:', error);
    throw error;
  }
};

// Update video
export const updateVideo = async (videoId: string, videoData: Partial<VideoData>): Promise<void> => {
  try {
    const videoRef = doc(db, VIDEOS_COLLECTION, videoId);
    const updateData = { ...videoData };
    
    // Convert Date to Timestamp if present
    if (updateData.publishDate) {
      updateData.publishDate = Timestamp.fromDate(updateData.publishDate) as any;
    }
    
    await updateDoc(videoRef, updateData);
  } catch (error) {
    console.error('Error updating video:', error);
    throw error;
  }
};

// Delete video
export const deleteVideo = async (videoId: string): Promise<void> => {
  try {
    const videoRef = doc(db, VIDEOS_COLLECTION, videoId);
    await deleteDoc(videoRef);
  } catch (error) {
    console.error('Error deleting video:', error);
    throw error;
  }
};

// Get video by ID
export const getVideoById = async (videoId: string): Promise<VideoData | null> => {
  try {
    const videoRef = doc(db, VIDEOS_COLLECTION, videoId);
    const videoSnap = await getDoc(videoRef);
    
    if (videoSnap.exists()) {
      return {
        id: videoSnap.id,
        ...videoSnap.data(),
        publishDate: videoSnap.data().publishDate?.toDate() || new Date()
      } as VideoData;
    }
    return null;
  } catch (error) {
    console.error('Error getting video:', error);
    throw error;
  }
};

// Upload video file to Firebase Storage
export const uploadVideoFile = async (file: File, videoId: string): Promise<string> => {
  try {
    const videoRef = ref(storage, `videos/${videoId}/${file.name}`);
    const snapshot = await uploadBytes(videoRef, file);
    const downloadURL = await getDownloadURL(snapshot.ref);
    return downloadURL;
  } catch (error) {
    console.error('Error uploading video file:', error);
    throw error;
  }
};

// Upload thumbnail to Firebase Storage
export const uploadThumbnail = async (file: File, videoId: string): Promise<string> => {
  try {
    const thumbnailRef = ref(storage, `thumbnails/${videoId}/${file.name}`);
    const snapshot = await uploadBytes(thumbnailRef, file);
    const downloadURL = await getDownloadURL(snapshot.ref);
    return downloadURL;
  } catch (error) {
    console.error('Error uploading thumbnail:', error);
    throw error;
  }
};

// Delete file from Firebase Storage
export const deleteFile = async (filePath: string): Promise<void> => {
  try {
    const fileRef = ref(storage, filePath);
    await deleteObject(fileRef);
  } catch (error) {
    console.error('Error deleting file:', error);
    throw error;
  }
};

// Extract video ID from YouTube URL
export const extractYouTubeId = (url: string): string | null => {
  const regExp = /^.*(youtu.be\/|v\/|u\/\w\/|embed\/|watch\?v=|&v=)([^#&?]*).*/;
  const match = url.match(regExp);
  return (match && match[2].length === 11) ? match[2] : null;
};

// Extract video ID from Vimeo URL
export const extractVimeoId = (url: string): string | null => {
  const regExp = /vimeo\.com\/(\d+)/;
  const match = url.match(regExp);
  return match ? match[1] : null;
};

// Generate thumbnail URL for YouTube
export const getYouTubeThumbnail = (videoId: string): string => {
  return `https://img.youtube.com/vi/${videoId}/maxresdefault.jpg`;
};

// Generate thumbnail URL for Vimeo
export const getVimeoThumbnail = async (videoId: string): Promise<string> => {
  try {
    const response = await fetch(`https://vimeo.com/api/v2/video/${videoId}.json`);
    const data = await response.json();
    return data[0]?.thumbnail_large || data[0]?.thumbnail_medium || '';
  } catch (error) {
    console.error('Error getting Vimeo thumbnail:', error);
    return '';
  }
};

// Get video info from YouTube API (requires API key)
export const getYouTubeVideoInfo = async (videoId: string, apiKey: string): Promise<{
  title: string;
  description: string;
  duration: number;
  thumbnail: string;
} | null> => {
  try {
    const response = await fetch(
      `https://www.googleapis.com/youtube/v3/videos?id=${videoId}&key=${apiKey}&part=snippet,contentDetails`
    );
    const data = await response.json();
    
    if (data.items && data.items.length > 0) {
      const video = data.items[0];
      return {
        title: video.snippet.title,
        description: video.snippet.description,
        duration: parseYouTubeDuration(video.contentDetails.duration),
        thumbnail: video.snippet.thumbnails.maxres?.url || video.snippet.thumbnails.high?.url
      };
    }
    return null;
  } catch (error) {
    console.error('Error getting YouTube video info:', error);
    return null;
  }
};

// Parse YouTube duration (PT1H2M3S format)
const parseYouTubeDuration = (duration: string): number => {
  const match = duration.match(/PT(?:(\d+)H)?(?:(\d+)M)?(?:(\d+)S)?/);
  if (!match) return 0;
  
  const hours = parseInt(match[1] || '0');
  const minutes = parseInt(match[2] || '0');
  const seconds = parseInt(match[3] || '0');
  
  return hours * 3600 + minutes * 60 + seconds;
};

